import { createStyles, makeStyles, Theme } from '@material-ui/core/styles';
import ChevronRightIcon from '@material-ui/icons/ChevronRight';
import ExpandMoreIcon from '@material-ui/icons/ExpandMore';
import FolderIcon from '@material-ui/icons/Folder';
import TreeItem from '@material-ui/lab/TreeItem';
import TreeView from '@material-ui/lab/TreeView';
import { FolderOpen as FolderOpenIcon } from 'mdi-material-ui';
import React, { useState, useEffect } from 'react';
import { RouteComponentProps, withRouter } from 'react-router-dom';
import FileIcon from './FileIcon';
import Tooltip from '@material-ui/core/Tooltip';
import Typography from '@material-ui/core/Typography';
import FileTreeNode from './FileTreeNode';

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    treeView: {
      color: theme.palette.text.secondary
    },
    fileIcon: {
      fontSize: '1rem'
    },
    treeItemLabel: {
      overflow: 'hidden',
      whiteSpace: 'nowrap',
      textOverflow: 'ellipsis'
    }
  })
);

function getNodeIdsFromFileTree(node, ids = []) {
  if (!node.leaf) {
    ids.push(node.id);
  }

  if (node.children) {
    node.children.forEach(child => getNodeIdsFromFileTree(child, ids));
  }

  return ids;
}

function renderTreeNode(node, classes, onClick) {
  let collapseIcon;
  let expandIcon;
  let icon;
  if (node.leaf) {
    icon = <FileIcon diffType={node.diffType} />;
  } else {
    collapseIcon = <FolderOpenIcon className={classes.fileIcon} color="inherit" />;
    expandIcon = <FolderIcon className={classes.fileIcon} color="inherit" />;
  }
  return (
    <TreeItem
      key={node.id}
      label={<Tooltip title={node.tooltip}><Typography component="span">{node.label}</Typography></Tooltip>}
      collapseIcon={collapseIcon}
      expandIcon={expandIcon}
      icon={icon}
      nodeId={node.id}
      onClick={() => onClick(node.id)}
      classes={{ label: classes.treeItemLabel }}
    >
      {node.children && node.children.filter(child => child.leaf || child.children.length > 0).map(child => renderTreeNode(child, classes, onClick))}
    </TreeItem>
  );
}

interface Props {
  diffs: object;
  executionPlan: string;
  onClick: (filename: string) => void;
}

function FileTreePanel(props: Props & RouteComponentProps<any>) {
  const { diffs, executionPlan, onClick } = props;
  const classes = useStyles({});
  const [fileTree, setFileTree] = useState(null);
  const [fileTreeNodeIds, setFileTreeNodeIds] = useState(null);

  useEffect(() => {
    const root = new FileTreeNode({ id: 'root' });

    if (diffs.workflow) {
      root.addChild(new FileTreeNode({
        id: diffs.workflow.id,
        label: 'Workflow',
        tooltip: 'Workflow',
        leaf: true,
        diffType: diffs.workflow.diffType
      }));
    }

    const diffTypes = {
      added: new FileTreeNode({ id: 'added', label: 'Added' }),
      removed: new FileTreeNode({ id: 'removed', label: 'Removed' }),
      changed: new FileTreeNode({ id: 'changed', label: 'Changed' })
    };

    const deployedDecksNode = new FileTreeNode({ id: 'deployedDecks', label: 'Deployed Decks' });

    const isTeardownRequest = executionPlan === 'TEARDOWN_ALL';

    if (!isTeardownRequest) {
      // Add diff types to root
      Object.keys(diffTypes).forEach(key => root.addChild(diffTypes[key]));
    } else {
      root.addChild(deployedDecksNode);
    }

    diffs.actions.forEach(action => {
      const parentNode = !isTeardownRequest ? diffTypes[action.diffType] : deployedDecksNode;
      const actionNode = new FileTreeNode({ id: action.actionName, label: action.deckName });
      parentNode.addChild(actionNode);

      if (action.deck) {
        const deckspeckNode = new FileTreeNode({
          id: action.deck.id,
          label: 'Deckspec',
          tooltip: `Deckspec (${action.actionName})`,
          leaf: true,
          diffType: action.deck.diffType
        });
        actionNode.addChild(deckspeckNode);
      }

      const cfNode = new FileTreeNode({ id: `${action.actionName}:CloudFormation`, label: 'CloudFormation' });
      actionNode.addChild(cfNode);

      const filesNode = new FileTreeNode({ id: `${action.actionName}:Files`, label: 'Files' });
      actionNode.addChild(filesNode);

      action.cloudFormation.forEach(cf => {
        cfNode.addChild(new FileTreeNode({
          id: cf.id,
          label: cf.filename,
          tooltip: `${cf.filename} (${action.actionName})`,
          leaf: true,
          diffType: cf.diffType
        }));
      });

      action.files.forEach(file => {
        filesNode.addChild(new FileTreeNode({
          id: file.id,
          label: file.filename,
          tooltip: `${file.filename} (${action.actionName})`,
          leaf: true,
          diffType: file.diffType
        }));
      });
    });

    setFileTree(root);
    setFileTreeNodeIds(getNodeIdsFromFileTree(root));
  }, [diffs, executionPlan]);

  return (
    <div>
      {fileTree && fileTreeNodeIds && <TreeView
        key={JSON.stringify(fileTreeNodeIds)}
        className={classes.treeView}
        defaultExpanded={fileTreeNodeIds}
        defaultCollapseIcon={<ExpandMoreIcon />}
        defaultExpandIcon={<ChevronRightIcon />}
      >
        {fileTree.children.map(child => renderTreeNode(child, classes, onClick))}
      </TreeView>}
    </div>
  );
};

export default withRouter(FileTreePanel);
